// pages/book/BookLoan.tsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getApiUrl, fetchWithAuth } from "../../api";
import { useAuthStore } from "../../store/authStore";

interface BookLoan {
  loan_id: number;
  book_title: string;
  barcode: string;
  loaned_at: string;
  returned_at: string | null;
  status: "loaned" | "returned";
}

export default function BookLoan() {
  const navigate = useNavigate();
  const { isLoggedIn } = useAuthStore();
  const [list, setList] = useState<BookLoan[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"" | "loaned" | "returned">("");
  const [maxLoans, setMaxLoans] = useState<number | null>(null);
  const [activeLoanCount, setActiveLoanCount] = useState(0);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/auth/login");
      return;
    }
    fetchLoans(filter);
  }, [filter]);

  const fetchLoans = async (statusFilter: "" | "loaned" | "returned") => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: "1", limit: "50" });
      if (statusFilter) params.set("status", statusFilter);
      const res = await fetchWithAuth(
        getApiUrl(`/marubit/api/book/loans/me?${params.toString()}`)
      );
      if (res.ok) {
        const data = await res.json();
        setList(data.data?.loans ?? []);
        setMaxLoans(data.data?.max_loans_per_member ?? null);
        setActiveLoanCount(data.data?.active_loan_count ?? 0);
      }
    } catch (e) {
      console.error("도서 대출 로딩 실패:", e);
    } finally {
      setLoading(false);
    }
  };

  const loanedCount = list.filter(b => b.status === "loaned").length;
  const returnedCount = list.filter(b => b.status === "returned").length;

  return (
    <div className="bg-[#fafafa] min-h-screen px-6 py-12">
      <div className="max-w-[1200px] mx-auto">

        {/* 타이틀 */}
        <div className="mb-8">
          <h1 className="text-[26px] font-extrabold text-[#5a4035] mb-2">도서 대출/반납</h1>
          <p className="text-[14px] text-[#6b5248] font-medium">
            대출 중인 도서와 반납 내역을 확인하세요.
          </p>
        </div>

        {/* 요약 카드 */}
        <div className="grid grid-cols-3 gap-4 mb-6 max-w-[600px]">
          {[
            { label: "대출 중", count: loanedCount, color: "text-[#f5a623]", unit: "권" },
            { label: "반납 완료", count: returnedCount, color: "text-[#887267]", unit: "권" },
            maxLoans != null
              ? { label: `대출 가능 (최대 ${maxLoans}권)`, count: Math.max(0, maxLoans - activeLoanCount), color: "text-[#5a8a5a]", unit: "권" }
              : { label: "대출 가능", count: "제한 없음", color: "text-[#5a8a5a]", unit: "" },
          ].map(item => (
            <div key={item.label} className="bg-white rounded-2xl border border-[#f0ebe7] shadow-[0_2px_12px_rgba(136,114,103,0.06)] px-6 py-4">
              <p className="m-0 text-[12px] text-gray-400 font-medium">{item.label}</p>
              <p className={`m-0 ${typeof item.count === "number" ? "text-[24px]" : "text-[18px]"} font-black mt-1 ${item.color}`}>
                {loading ? "-" : item.count}
                {item.unit && <span className="text-[14px] font-bold ml-1">{item.unit}</span>}
              </p>
            </div>
          ))}
        </div>

        {/* 목록 */}
        <div className="bg-white rounded-2xl border border-[#f0ebe7] shadow-[0_2px_12px_rgba(136,114,103,0.06)] overflow-hidden">
          <div className="px-6 py-4 border-b border-[#f5f0ec] flex items-center justify-between">
            <p className="m-0 text-[15px] font-bold text-[#3d2f2a]">대출 내역</p>
            <div className="flex gap-1">
              {([["", "전체"], ["loaned", "대출중"], ["returned", "반납완료"]] as const).map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => setFilter(val)}
                  className={[
                    "px-3 py-1 rounded-full text-[12px] font-semibold border-none cursor-pointer transition-all",
                    filter === val ? "bg-[#887267] text-white" : "bg-[#f5f0ec] text-gray-500 hover:bg-[#ede8e4]",
                  ].join(" ")}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="py-16 text-center text-[14px] text-gray-400">불러오는 중...</div>
          ) : list.length === 0 ? (
            <div className="py-16 text-center text-[14px] text-gray-400">내역이 없습니다.</div>
          ) : (
            <div className="divide-y divide-[#f5f0ec]">
              {list.map(item => {
                const isLoaned = item.status === "loaned";
                return (
                  <div key={item.loan_id} className="px-6 py-4 hover:bg-[#fdf9f7] transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="m-0 text-[15px] font-bold text-[#3d2f2a] truncate">{item.book_title}</p>
                        <p className="m-0 text-[12px] text-gray-400 mt-[2px]">바코드: {item.barcode}</p>
                        <div className="flex items-center gap-3 mt-2">
                          <span className="text-[12px] text-gray-400">
                            대출일 <span className="text-[#3d2f2a] font-medium">{item.loaned_at.slice(0, 10)}</span>
                          </span>
                          {item.returned_at && (
                            <span className="text-[12px] text-gray-400">
                              반납일 <span className="text-[#3d2f2a] font-medium">{item.returned_at.slice(0, 10)}</span>
                            </span>
                          )}
                        </div>
                      </div>
                      <span className={`inline-block px-3 py-[3px] rounded-full text-[12px] font-bold shrink-0 ${
                        isLoaned ? "bg-[#fff3e0] text-[#f5a623]" : "bg-[#f0ebe7] text-[#887267]"
                      }`}>
                        {isLoaned ? "대출중" : "반납완료"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}